import { format, formatDistanceToNow, isAfter, isBefore, addDays, parseISO } from 'date-fns'

// Parse date string as local date (fixes timezone issues with date-only strings)
const parseLocalDate = (dateString) => {
  if (!dateString) return null
  // If it's a date-only string (YYYY-MM-DD), parse as local date
  if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number)
    return new Date(year, month - 1, day)
  }
  // Otherwise use parseISO for full datetime strings
  return typeof dateString === 'string' ? parseISO(dateString) : dateString.toDate ? dateString.toDate() : dateString
}

// Date formatting
export const formatDate = (date, pattern = 'MMMM d, yyyy') => {
  if (!date) return ''
  const dateObj = parseLocalDate(date)
  return format(dateObj, pattern)
}

export const formatTime = (date, pattern = 'h:mm a') => {
  if (!date) return ''
  const dateObj = parseLocalDate(date)
  return format(dateObj, pattern)
}

export const formatDateTime = (date) => {
  return `${formatDate(date)} at ${formatTime(date)}`
}

export const formatRelativeTime = (date) => {
  if (!date) return ''
  const dateObj = parseLocalDate(date)
  return formatDistanceToNow(dateObj, { addSuffix: true })
}

// Check if date is in the future
export const isFutureDate = (date) => {
  const dateObj = parseLocalDate(date)
  return isAfter(dateObj, new Date())
}

// Check if date is in the past
export const isPastDate = (date) => {
  const dateObj = parseLocalDate(date)
  return isBefore(dateObj, new Date())
}

// Get countdown to date
export const getCountdown = (targetDate) => {
  const target = parseLocalDate(targetDate)
  const now = new Date()
  const diff = target - now

  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((diff % (1000 * 60)) / 1000)

  return { days, hours, minutes, seconds, expired: false }
}

// String utilities
export const truncate = (str, length = 100) => {
  if (!str || str.length <= length) return str
  return str.slice(0, length).trim() + '...'
}

export const capitalize = (str) => {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export const slugify = (str) => {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

// URL utilities
export const getYouTubeId = (url) => {
  if (!url) return null
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = url.match(regExp)
  return match && match[2].length === 11 ? match[2] : null
}

export const getYouTubeThumbnail = (url, quality = 'maxresdefault') => {
  const videoId = getYouTubeId(url)
  if (!videoId) return null
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`
}

export const getVimeoId = (url) => {
  if (!url) return null
  const regExp = /vimeo\.com\/(\d+)/
  const match = url.match(regExp)
  return match ? match[1] : null
}

// Form validation helpers
export const isValidEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return re.test(email)
}

export const isValidPhone = (phone) => {
  const re = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/
  return re.test(phone.replace(/\s/g, ''))
}

// Only allow same-site relative redirect targets (e.g. /workers/login?next=...).
// Anything absolute, protocol-relative, or backslash-tricked falls back.
export const safeNext = (next, fallback = '/workers') => {
  if (typeof next !== 'string') return fallback
  if (!next.startsWith('/') || next.startsWith('//') || next.includes('\\')) {
    return fallback
  }
  return next
}

// Normalize a US phone number to E.164 (+1XXXXXXXXXX); null if not valid
export const normalizePhone = (raw) => {
  if (!raw) return null
  const digits = String(raw).replace(/\D/g, '')
  if (digits.length === 10) return `+1${digits}`
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`
  return null
}

// Today's date as a local YYYY-MM-DD string (matches assignment date format)
export const todayISO = () => {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

// Array utilities
export const groupBy = (array, key) => {
  return array.reduce((result, item) => {
    const groupKey = typeof key === 'function' ? key(item) : item[key]
    if (!result[groupKey]) {
      result[groupKey] = []
    }
    result[groupKey].push(item)
    return result
  }, {})
}

export const sortBy = (array, key, direction = 'asc') => {
  return [...array].sort((a, b) => {
    const aVal = typeof key === 'function' ? key(a) : a[key]
    const bVal = typeof key === 'function' ? key(b) : b[key]

    if (direction === 'asc') {
      return aVal < bVal ? -1 : aVal > bVal ? 1 : 0
    }
    return aVal > bVal ? -1 : aVal < bVal ? 1 : 0
  })
}

export const uniqueBy = (array, key) => {
  const seen = new Set()
  return array.filter(item => {
    const val = typeof key === 'function' ? key(item) : item[key]
    if (seen.has(val)) return false
    seen.add(val)
    return true
  })
}

// Number utilities
export const formatNumber = (num) => {
  return new Intl.NumberFormat().format(num)
}

export const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount)
}

// File utilities
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export const getFileExtension = (filename) => {
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2)
}

export const isImageFile = (filename) => {
  const ext = getFileExtension(filename).toLowerCase()
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)
}

// Local storage helpers
export const storage = {
  get: (key, defaultValue = null) => {
    try {
      const item = localStorage.getItem(key)
      return item ? JSON.parse(item) : defaultValue
    } catch {
      return defaultValue
    }
  },
  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value))
    } catch (e) {
      console.error('Error saving to localStorage:', e)
    }
  },
  remove: (key) => {
    localStorage.removeItem(key)
  },
}

// Generate unique ID
export const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2)
}

// Debounce function
export const debounce = (func, wait) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

// Throttle function
export const throttle = (func, limit) => {
  let inThrottle
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}

// Class name utility (alternative to clsx for simple cases)
export const cn = (...classes) => {
  return classes.filter(Boolean).join(' ')
}
